import { config } from '../../../../config';
import type { AdminReportListItem, Issue } from '../../../api/DigiobcanApi';
import AdminReportCategories from '../../../components/AdminReportCategories';
import AdminReportStatusItem from '../../../components/AdminReportStatusItem';
import AdminReportsAiDryRunModal, {
  AI_DRY_RUN_MAX,
  type AiDryRunReport,
} from '../../../components/AdminReportsAiDryRunModal';
import AdminReportsArchiveModal, {
  type ArchiveReport,
} from '../../../components/AdminReportsArchiveModal';
import AdminReportsMergeModal, {
  type MergeReport,
} from '../../../components/AdminReportsMergeModal';
import DeadlineCell from '../../../components/DeadlineCell';
import ErbThumbnail from '../../../components/ErbThumbnail';
import { ExplicitIcon } from '../../../components/ExplicitIcon';
import { userLabel } from '../../../components/UserAvatarStack';
import { adminGetReports } from '../../../features/queries/adminGetReports';
import { adminGetReportsAssignees } from '../../../features/queries/adminGetReportsAssignees';
import {
  meGetReportCategories,
  useCanAssignSomewhere,
  useCanManageReportCategory,
  useIsApproverSomewhere,
  useIsCategoryManagerSomewhere,
  useIsInAssigningCategory,
} from '../../../features/queries/meGetReportCategories';
import { entityId, shortId } from '../../../lib/ids';
import { rememberListSearch } from '../../../lib/listSearch';
import { useInfiniteQ, useLoadPagesThrough, useQ } from '../../../lib/query';
import { useLayout } from '../../../providers/LayoutProvider';
import type { GeoLocation } from '../../../providers/LocationProvider';
import { useResponsive } from '../../../providers/ResponsiveProvider';
import { __ } from '../../../utils';
import ReportCategoriesButton from './ReportCategoriesButton';
import ReportsExportButton from './ReportsExportButton';
import ReportsLocationFilter, {
  type LocationFilterValue,
} from './ReportsLocationFilter';
import { useAuth, useAuthRoles } from '@presov.sk/presov-auth-react';
import {
  Box,
  Button,
  DataTable,
  EmptyState,
  FilterBar,
  FilterChip,
  FilterPanel,
  Pagination,
  optionLabel,
  selectedValues,
  Stack,
  Tooltip,
  Typography,
  useUrlFilters,
  type Column,
  type FilterField,
  type FilterValue,
  type SortKey,
} from '@presov.sk/presov-ui-react';
import { formatDate } from 'date-fns';
import { encode } from 'pluscodes';
import React from 'react';
import { useLocation, useSearchParams } from 'react-router';

/**
 * Stav nálezu ako filter ponúka **skupiny**, nie surové stavy.
 *
 * Skupina „Podané" mala do zrušenia stavu `possible_duplicate` dvoch členov -
 * ten stav mal v celej aplikácii ten istý názov aj farbu ako `open`, takže ako
 * dve položky zoznamu by boli nerozlíšiteľné. Odvtedy je návrh na duplicitu
 * príznak nálezu (`has_possible_duplicates`), nie jeho stav, a skupina má
 * jediného člena. Štruktúra zostáva: je to filter podľa toho, čo úradník
 * hľadá, nie výpis enumu.
 *
 * Duplikát medzi nimi nie je: je to rýchly prepínač „Duplikáty" vedľa „Po
 * lehote" (`DUPLICATES_LABEL`). Úradník ho hľadá ako samostatnú otázku („čo
 * sme zlúčili"), nie ako jeden zo stavov práce, ktorá čaká.
 *
 * Názvy skupín sa musia zhodovať s tým, čo o stave hovorí `getReportStatusMeta`
 * ([[AdminReportStatusItem]]) - filter a štítok v riadku pod ním sú ten istý
 * stav. Preto tu nie je množné číslo („Vybavujú sa"), ale „V riešení".
 */
const STATUS_GROUPS: {
  value: string;
  label: string;
  statuses: Issue['status'][];
}[] = [
  { value: 'open', label: __('Podané'), statuses: ['open'] },
  { value: 'accepted', label: __('V riešení'), statuses: ['accepted'] },
  { value: 'resolved', label: __('Vyriešené'), statuses: ['resolved'] },
];

const OVERDUE_LABEL = __('Po lehote');

const DUPLICATES_LABEL = __('Duplikáty');

const ARCHIVED_LABEL = __('Archivované');

const AWAITING_APPROVAL_LABEL = __('Na schválenie');
const AWAITING_REVISION_LABEL = __('Úprava vyjadrenia');
const ASSIGNED_TO_ME_LABEL = __('Pridelené mne');
const ASSIGNEE_LABEL = __('Riešiteľ');

/**
 * „Nepridelené" vo filtri riešiteľa: v adrese `nepridelene`, na API
 * vyhradená hodnota `none` (nález v prideľujúcej kategórii, ktorý ešte nikto
 * nemá). Je to otázka rozdeľovateľa - čo treba rozdať - a zároveň druhá
 * možnosť ponuky: výber s jedinou možnosťou knižnica nekreslí, takže kým mal
 * pridelené len jeden človek, filter by sa vôbec neukázal.
 */
const UNASSIGNED_VALUE = 'nepridelene';
const UNASSIGNED_API_VALUE = 'none';

/**
 * Štítok „čaká na mňa" pred kategóriami riadku. Farbou je to aktívna položka
 * žltého pásu administrácie, nie `StatusChip warning`: ten má plochu
 * `warning.surface`, teda presne tú, ktorou je podfarbený riadok, a štítok
 * v ňom zanikal.
 */
const AwaitingMePill: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => (
  <Box
    component='span'
    sx={(theme) => ({
      flex: 'none',
      display: 'inline-flex',
      alignItems: 'center',
      height: theme.controlHeight.tag,
      px: 1,
      borderRadius: `${theme.shape.borderRadiusSm}px`,
      fontSize: theme.typography.pxToRem(12),
      fontWeight: 700,
      whiteSpace: 'nowrap',
      backgroundColor:
        theme.palette.yellow.surfaceStrong ?? theme.palette.yellow.surface,
      color: theme.palette.yellow.surfaceText ?? theme.palette.yellow.extraDark,
    })}
  >
    {children}
  </Box>
);

/**
 * Kľúč stĺpca → `orderBy` API. V URL stojí kľúč stĺpca (`sort=-inserted_at`),
 * nie parameter API: adresa je stav obrazovky, nie tvar dotazu.
 */
const SORT_COLUMNS = {
  votes_count: 'votes',
  inserted_at: 'date',
  distance: 'location',
  deadline_at: 'deadline',
} as const;

type SortableKey = keyof typeof SORT_COLUMNS;

const SORTABLE_KEYS = Object.keys(SORT_COLUMNS);

/**
 * Predvolene **najkratšia lehota navrchu** - práca, ktorej čas beží najskôr.
 * Je to len druhý kľúč: prvý je vždy stav podnetu (podané a v riešení spolu,
 * potom vyriešené, ...) a ten drží API, takže zvolené zoradenie ho nikdy
 * nerozhodí.
 */
const DEFAULT_SORT: SortKey[] = [{ key: 'deadline_at', dir: 'asc' }];

/**
 * Bežný zoznam duplikáty neukazuje - sú to zlúčené nálezy, ktoré sa riešia
 * v origináli, a medzi prácou, ktorá čaká, len zavadzali. Vidno ich len cez
 * prepínač „Duplikáty". Posiela sa to ako filter stavu, lebo API bez neho
 * vracia všetky stavy; podnet s duplikátom **a** iným nálezom v zozname
 * zostáva.
 */
const LIVE_STATUSES: Issue['status'][] = ['open', 'accepted', 'resolved'];

const formatDistanceLabel = (distance: string | number) => {
  const meters =
    typeof distance === 'number' ? distance : parseInt(distance, 10);

  if (Number.isNaN(meters)) {
    return String(distance);
  }

  if (meters >= 1000) {
    const km = meters / 1000;
    return `${Number.isInteger(km) ? km : km.toFixed(1)} km`;
  }

  return `${Math.round(meters)} m`;
};

/**
 * Vybraný riadok. Nesie všetky prístupné nálezy, nie len tie označiteľné:
 * z výberu sa v modále určí, ktorý podnet je primárny, a to potrebuje aj
 * svoje prijaté nálezy - práve tie sú rodičmi duplikátov.
 */
type SelectedReport = MergeReport & {
  /**
   * Archivovaný? Zoznam môže miešať archivované so živými (archív je člen
   * filtra stavu), takže hromadná akcia sa nerozhoduje podľa filtra, ale
   * podľa toho, čo je naozaj vo výbere.
   */
  archived: boolean;
};

const toMergeReport = (report: AdminReportListItem): SelectedReport => ({
  reportId: report.id,
  title: report.title,
  issues: report.issues ?? [],
  archived: !!report.archived_at,
});

/**
 * Stavy nálezu pre API. Nič nevybraté = bežné stavy bez duplikátov
 * (`LIVE_STATUSES`) - **okrem samotného archívu**: tam prázdny zoznam stavov
 * znamená „celý archív", a keby sa poslal `LIVE_STATUSES`, API by ho spojilo
 * so živými podnetmi (archív je člen filtra stavu).
 */
const reportStatuses = (filters: FilterValue) => {
  const chosen = [
    ...STATUS_GROUPS.filter((group) =>
      selectedValues(filters, 'status').includes(group.value),
    ).flatMap((group) => group.statuses),
    ...(filters.duplicates ? (['duplicate'] as Issue['status'][]) : []),
  ];

  if (chosen.length || filters.archived) return chosen;
  return LIVE_STATUSES;
};

/** `2026-08-24` → hranice dňa, lebo API berie `date-time`. */
const dayStart = (value?: string) =>
  value ? new Date(`${value}T00:00:00.000Z`).toISOString() : undefined;
const dayEnd = (value?: string) =>
  value ? new Date(`${value}T23:59:59.999Z`).toISOString() : undefined;

type ReportRow = AdminReportListItem & { distance?: number };

/**
 * Správa podnetov: `FilterBar` **v hlavičke stránky** (slot `page.filters`),
 * tabuľka na plátne pod ňou a `Pagination` v pripnutej pätičke shellu.
 *
 * **Riadok je podnet, nie nález.** Nálezy podnetu sú v ňom ako štítky
 * kategórií so stavom ([[AdminReportCategories]]) a `status` je z nich
 * derivovaný na API. Keď zoznam ukáže ten istý podnet viackrát, nie je to
 * chyba tejto obrazovky - beží proti API bez tohto zoskupenia.
 *
 * Filtre žijú v URL, nie v stave komponenty: odkaz na vyfiltrovaný zoznam sa
 * dá poslať kolegovi a `history.back()` sa vráti k predošlému výberu.
 */
const ReportsAdminPage: React.FC = () => {
  const { authUser } = useAuth();
  const { hasPermission } = useAuthRoles();
  const [searchParams, setSearchParams] = useSearchParams();
  const { pathname, search: urlSearch } = useLocation();
  const { isMd } = useResponsive();
  const { setFooter, setAside, setPageFilters, setActions } = useLayout();

  const [panelOpen, setPanelOpen] = React.useState(false);
  const [selectedReports, setSelectedReports] = React.useState<
    SelectedReport[]
  >([]);
  const [archiveModalReports, setArchiveModalReports] = React.useState<
    ArchiveReport[]
  >([]);
  const [mergeModalReports, setMergeModalReports] = React.useState<
    MergeReport[]
  >([]);
  const [aiDryRunReports, setAiDryRunReports] = React.useState<
    AiDryRunReport[]
  >([]);

  const { data: userCategories = [] } = useQ(
    meGetReportCategories,
    { userId: authUser.clientAccountId },
    {
      // Čakáreň patrí na koniec ponuky: nie je to útvar, ktorý niečo rieši,
      // ale kategória, do ktorej podnet padne, kým ho niekto nezaradí.
      // Ktorá to je, nesie príznak `is_default` z API - kým to bola hodnota
      // v konfigurácii, musela sa zhodovať s databázou a nemala ako.
      select: (data) =>
        data.sort((a, b) => {
          if (a.is_default) return 1;
          if (b.is_default) return -1;
          return 0;
        }),
    },
  );

  // Cesta späť z detailu je statická, takže by zoznam otvorila odznova a výber
  // by sa stratil. Odkladá sa preto celý `search` a detail si ho pripojí -
  // detail v [listSearch](../../../lib/listSearch.ts).
  React.useEffect(() => {
    rememberListSearch(pathname, urlSearch);
  }, [pathname, urlSearch]);

  // ── URL ↔ hodnoty filtrov ──────────────────────────────────────────────────

  /**
   * Vidí prihlásený úradník archivované podnety?
   *
   * Stojí to nad schémou filtrov, lebo prepínač „Archivované" v nej **vôbec
   * nie je**, keď oprávnenie chýba - nestačí ho zobraziť neaktívny. Pole,
   * ktoré schéma nepozná, `useUrlFilters` nečíta ani nezapisuje, takže ručne
   * dopísané `?archived=true` v adrese nespôsobí nič. A keby aj, server ho
   * odmietne (403 `REPORTS_ARCHIVED_ACCESS_FORBIDDEN`) - toto je pohodlie,
   * nie ochrana.
   */
  const canReadArchived = React.useMemo(
    () =>
      hasPermission({
        client_id: config.CLIENT_ID,
        permission: 'pruser:reports:archived:read',
      }),
    [hasPermission],
  );

  const canArchive = React.useMemo(
    () =>
      hasPermission({
        client_id: config.CLIENT_ID,
        permission: 'pruser:reports:archive',
      }),
    [hasPermission],
  );

  /**
   * Skúšobný beh AI stojí na tom istom oprávnení ako prehľad rozhodnutí AI -
   * výsledok sa dá pozrieť len tam, a na podnete beh nič nemení. Preto ho
   * nebrzdí ani cudzia kategória, ani archív.
   */
  const canRunAi = React.useMemo(
    () =>
      hasPermission({
        client_id: config.CLIENT_ID,
        permission: 'pruser:ai-decisions:read',
      }),
    [hasPermission],
  );

  /**
   * Je vo výbere podnet, ktorý úradník len vidí?
   *
   * `pruser:report-categories:read-all` (sekretariáty) ukazuje v zozname aj
   * podnety z cudzích kategórií, ale hromadné akcie nad nimi sú zápis a API
   * ich odmieta 403. Stačí jedna vlastná kategória z ktoréhokoľvek nálezu -
   * zlúčenie aj archivácia sa robia nad podnetom ako celkom.
   */
  const canManageCategory = useCanManageReportCategory();

  /**
   * Schvaľuje prihlásený úradník vyjadrenia aspoň v jednej kategórii (alebo ju
   * spravuje)? Len vtedy dáva filter „Na schválenie" zmysel - ostatným by vždy
   * vrátil prázdny zoznam. Rovnako ako „Archivované" v schéme bez toho nie je
   * vôbec, takže ručne dopísaný parameter v adrese nespôsobí nič.
   */
  const isApprover = useIsApproverSomewhere();

  /**
   * Je prihlásený v kategórii, ktorá funguje na prideľovaní? Len tam filter
   * „Pridelené mne" niečo vráti - inde sa nálezy nikomu neprideľujú. Rovnako
   * ako „Na schválenie" bez toho v schéme nie je.
   */
  const isInAssigningCategory = useIsInAssigningCategory();
  const canAssign = useCanAssignSomewhere();
  const canFilterAssignee = React.useMemo(
    () =>
      canAssign ||
      (isInAssigningCategory &&
        hasPermission({
          client_id: config.CLIENT_ID,
          permission: 'pruser:report-categories:read-all',
        })),
    [canAssign, isInAssigningCategory, hasPermission],
  );

  /**
   * Ponuka filtra „Riešiteľ" - ľudia s prideleným nálezom, ktorý prihlásený
   * vidí (`GET /admin/reports-assignees`). Na rozdiel od „Pridelené mne" len
   * pre toho, kto v prideľujúcej kategórii smie prideľovať (`can_assign`),
   * a pre sekretariáty (`pruser:report-categories:read-all`): tí vidia
   * nálezy všetkých riešiteľov a sledujú, kto čo má. Obyčajný riešiteľ vidí
   * iba svoje, takže by filtroval sám seba. Schvaľovateľ (`can_approve`)
   * nálezy kategórie vidí tiež, ale filter zámerne nedostane - schvaľuje
   * vyjadrenia, nie rozdelenie práce, a na to má „Na schválenie". Nie je to
   * ochrana - ponuku aj zoznam aj tak počíta API z toho, čo volajúci vidí.
   * Kto nemá pridelené nič, v ponuke nie je - filter na neho by vrátil
   * prázdny zoznam.
   *
   * V adrese stojí **holý koncový segment** účtu, ako pri kategóriách
   * ([[zoznamy]]). Späť na celé `client_acc_id` ho vracia táto ponuka, nie
   * predpona: účty nie sú entity DigiObčana a pevnú predponu nemajú.
   */
  const { data: assigneesData, isLoading: assigneesLoading } = useQ(
    adminGetReportsAssignees,
    {},
    { enabled: canFilterAssignee, staleTime: 60 * 1000 },
  );
  const assignees = React.useMemo(
    () => assigneesData?.users ?? [],
    [assigneesData],
  );

  /**
   * Smie prihlásený spravovať kategórie podnetov - celomestsky, alebo ako
   * správca aspoň jednej? Tá istá podmienka, akou ich routy stráži
   * `ReportCategoriesGuard`, takže tlačidlo nevedie na presmerovanie domov.
   */
  const isCategoryManager = useIsCategoryManagerSomewhere();
  const canManageCategories = React.useMemo(
    () =>
      !!isCategoryManager ||
      hasPermission({
        client_id: config.CLIENT_ID,
        permission: 'pruser:report-categories:manage',
      }),
    [hasPermission, isCategoryManager],
  );
  const selectionHasUnmanaged = selectedReports.some(
    (report) =>
      !report.issues.some((issue) => canManageCategory(issue.category_id)),
  );

  /**
   * Filtre, hľadanie, zoradenie aj stranu drží **URL** a stará sa o to
   * `useUrlFilters` z knižnice. Do schémy patria len polia, ktoré vie
   * vyjadriť; `location` a `distance` sú mimo nej, takže si ich stránka
   * zapisuje sama cez `patch` - hook cudzie parametre nečíta ani neprepisuje.
   */
  const filterFields: FilterField[] = React.useMemo(
    () => [
      {
        key: 'category',
        label: __('Kategória'),
        type: 'select',
        // Hodnota je identifikátor, popis názov: v adrese má stáť stabilný
        // kľúč, inak by premenovanie kategórie zhodilo uložený odkaz.
        // Z `cat:Doprava` ide do adresy holý koncový segment - predponu
        // v nej hlási názov parametra (`?category=Doprava`), takže bola len
        // šum, a k tomu zakódovaný (`cat%3ADoprava`). Späť na appkovú podobu
        // ju vracia `entityId` pri zostavovaní dotazu; ten znesie aj starší
        // odkaz, v ktorom predpona ešte stojí.
        options: userCategories.map((category) => ({
          value: shortId(category.category_id),
          label: category.name,
        })),
        quick: true,
      },
      {
        key: 'status',
        label: __('Stav nálezu'),
        type: 'select',
        options: STATUS_GROUPS.map((group) => ({
          value: group.value,
          label: group.label,
        })),
        quick: true,
      },
      // Mimo lišty rýchlych filtrov - tá je plná a riešiteľa hľadá hlavne
      // rozdeľovateľ. Panel kreslí skupiny v poradí schémy a toto je prvé
      // pole bez `group`, takže je v stĺpci filtrov navrchu.
      ...(canFilterAssignee
        ? [
            {
              key: 'assignee',
              label: ASSIGNEE_LABEL,
              type: 'select',
              options: [
                { value: UNASSIGNED_VALUE, label: __('Nepridelené') },
                ...assignees.map((user) => ({
                  value: shortId(user.id),
                  label: userLabel(user),
                })),
              ],
            } as FilterField,
          ]
        : []),
      { key: 'overdue', label: OVERDUE_LABEL, type: 'toggle', quick: true },
      ...(isApprover
        ? [
            {
              key: 'awaitingMyApproval',
              label: AWAITING_APPROVAL_LABEL,
              type: 'toggle',
              quick: true,
            } as FilterField,
          ]
        : []),
      ...(isInAssigningCategory
        ? [
            {
              key: 'assignedToMe',
              label: ASSIGNED_TO_ME_LABEL,
              type: 'toggle',
              quick: true,
            } as FilterField,
          ]
        : []),
      {
        key: 'duplicates',
        label: DUPLICATES_LABEL,
        type: 'toggle',
        quick: true,
      },
      // Archív je ďalší člen filtra stavu: sám ukáže len archivované, so
      // stavom alebo duplikátmi archivované **aj** živé v danom stave.
      // Archivovaný riadok hlási stĺpec „Stav podnetu".
      ...(canReadArchived
        ? [
            {
              key: 'archived',
              label: ARCHIVED_LABEL,
              type: 'toggle',
              quick: true,
            } as FilterField,
          ]
        : []),
      {
        key: 'dateFrom',
        label: __('Vytvorené od'),
        type: 'date',
        group: __('Dátum vytvorenia'),
      },
      {
        key: 'dateTo',
        label: __('Vytvorené do'),
        type: 'date',
        group: __('Dátum vytvorenia'),
      },
    ],
    [
      userCategories,
      canReadArchived,
      isApprover,
      isInAssigningCategory,
      canFilterAssignee,
      assignees,
    ],
  );

  const {
    query,
    setQuery,
    search,
    filters,
    setFilters,
    sort,
    setSort,
    page: pageIndex,
    setPage: setPageIndex,
    activeCount,
    clearAll: clearFilters,
    patch,
  } = useUrlFilters({
    searchParams,
    setSearchParams,
    fields: filterFields,
    sortable: SORTABLE_KEYS,
    defaultSort: DEFAULT_SORT,
  });

  const location = React.useMemo<GeoLocation | undefined>(() => {
    const raw = searchParams.get('location');
    return raw
      ? (raw.split(',').map((part) => parseFloat(part)) as GeoLocation)
      : undefined;
  }, [searchParams]);
  const distance = searchParams.get('distance') ?? undefined;

  const setLocationFilter = React.useCallback(
    (value: LocationFilterValue) => {
      patch({
        location: value.location ? value.location.join(',') : undefined,
        distance: value.location ? value.distance : undefined,
        // Bez lokality nie je od čoho merať vzdialenosť - zoradenie podľa nej
        // by ostalo visieť na parametri, ktorý sa už neposiela.
        ...(value.location || !sort.some((item) => item.key === 'distance')
          ? {}
          : { sort: undefined }),
      });
    },
    [patch, sort],
  );

  const clearAll = React.useCallback(() => {
    clearFilters();
    patch({ location: undefined, distance: undefined });
  }, [clearFilters, patch]);

  // ── Dáta ───────────────────────────────────────────────────────────────────

  const limit = config.CUSTOM_LIMIT.ADMIN.REPORTS;

  const canReadReports = React.useMemo(
    () =>
      hasPermission({
        client_id: config.CLIENT_ID,
        permission: 'pruser:reports:read',
      }),
    [hasPermission],
  );

  /**
   * Preklad stavu obrazovky na parametre API - **jedno miesto pre zoznam aj
   * pre export**.
   *
   * Stálo to priamo v `useInfiniteQ` a tlačidlo tlače si ho muselo poskladať
   * druhýkrát. Dve kópie tohto prekladu znamenajú, že sa raz rozídu: výtlačok
   * by potom nebol tým zoznamom, ktorý mal úradník na obrazovke, a nebolo by
   * to na ňom vidieť. `limit` je tu zámerne mimo - zoznam stránkuje, export
   * berie všetko.
   */
  const reportsQuery = React.useMemo(
    () => ({
      q: search,
      // API radí podľa jedného kľúča, `DataTable` ohlási pole - berie sa
      // posledný klik. Preklad kľúča stĺpca na parameter API je tu, nie v URL.
      orderBy:
        SORT_COLUMNS[
          (sort[sort.length - 1]?.key ?? 'deadline_at') as SortableKey
        ],
      orderDirection: sort[sort.length - 1]?.dir ?? 'asc',
      category: selectedValues(filters, 'category').map((value) =>
        entityId(value, 'category'),
      ),
      // Jedna položka filtra môže znamenať viac stavov nálezu, takže sa
      // skupiny rozbaľujú tu.
      // „Duplikáty" je prepínač, ale na API je to ďalší stav v tom istom
      // zozname - so zvoleným stavom teda ukáže oboje, ako keď bol Duplikát
      // položkou tohto výberu.
      status: reportStatuses(filters),
      dateFrom: dayStart((filters.dateFrom as string) || undefined),
      dateTo: dayEnd((filters.dateTo as string) || undefined),
      location:
        location && distance
          ? `${location[0]},${location[1]},${distance}`
          : undefined,
      // `false` by na API znamenalo „len v lehote", preto posielame len `true`.
      overdue: filters.overdue ? true : undefined,
      // Tu naopak `false` znamená to isté ako neuvedené (bežný zoznam), ale
      // posielať ho netreba - a bez oprávnenia by ho server odmietol.
      archived: filters.archived ? true : undefined,
      // Len `true`: „nič na mňa nečaká" nie je otázka, ktorú si niekto kladie.
      awaitingMyApproval:
        isApprover && filters.awaitingMyApproval ? true : undefined,
      // Server hľadá nález pridelený volajúcemu, ktorý zároveň sedí na
      // kategóriu a stav - nie „podnet, v ktorom mám niečo pridelené".
      assignedToMe:
        isInAssigningCategory && filters.assignedToMe ? true : undefined,
      // Z adresy holý segment, na API celé `client_acc_id` z ponuky. Kto
      // v nej nie je (pridelené už nemá), ide tak, ako stojí v adrese - API
      // na neho poctivo vráti prázdny zoznam, nie celý.
      assignee: canFilterAssignee
        ? selectedValues(filters, 'assignee').map((value) =>
            value === UNASSIGNED_VALUE
              ? UNASSIGNED_API_VALUE
              : (assignees.find((user) => shortId(user.id) === value)?.id ??
                value),
          )
        : undefined,
    }),
    [
      search,
      sort,
      filters,
      location,
      distance,
      isApprover,
      isInAssigningCategory,
      canFilterAssignee,
      assignees,
    ],
  );

  // S riešiteľom v adrese sa zoznam pýta, až keď je ponuka načítaná - inak
  // by odišiel s holým segmentom a na okamih ukázal prázdny výsledok.
  const waitingForAssignees =
    assigneesLoading && selectedValues(filters, 'assignee').length > 0;

  const {
    data = { pages: [] },
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    isLoading,
    refetch: refetchData,
  } = useInfiniteQ(
    adminGetReports,
    {
      limit,
      ...reportsQuery,
    },
    {
      initialPageParam: 0,
      getNextPageParam: (lastPage) =>
        lastPage.hasNextOffset ? lastPage.offset + limit : undefined,
      enabled: !!canReadReports && !waitingForAssignees,
    },
  );

  // F5 na `?page=2`: nekonečný dotaz začína od prvej strany, takže sa musí
  // dočítať až po tú z adresy (`useLoadPagesThrough`).
  useLoadPagesThrough({
    pageIndex,
    loadedPages: data.pages.length,
    hasNextPage: !!hasNextPage,
    isFetching: isLoading || isFetchingNextPage,
    fetchNextPage,
    setPageIndex,
  });

  const reports = React.useMemo(
    () => (data.pages[pageIndex]?.reports ?? []) as ReportRow[],
    [data.pages, pageIndex],
  );

  /**
   * Pozeráme sa do archívu?
   *
   * Výber riadkov je v oboch zobrazeniach, ale **ponúka v nich iné akcie**:
   * v bežnom zozname „Označiť ako duplikáty" a „Archivovať", v archíve len
   * „Obnoviť". Zlučovanie by na archivovanom podnete padlo na `409`, takže sa
   * tam neponúka vôbec.
   *
   * Štítok „archivované" v riadku tu zámerne nie je: v tomto zobrazení je
   * archivovaný každý riadok a čip v lište to hlási raz, kým štítok by ho
   * zopakoval pätnásťkrát.
   */
  // „Len archív" - archív zapnutý bez stavu či duplikátov. So stavom je
  // zoznam zmesou archivovaných a živých (viď `archived` v `reportsQuery`).
  const viewingArchive =
    !!filters.archived &&
    !selectedValues(filters, 'status').length &&
    !filters.duplicates;
  const viewingApprovals = isApprover && !!filters.awaitingMyApproval;

  // Výber sa nesmie preniesť z bežného zoznamu do archívu ani naopak: riadky
  // sú iné, ale `selectedReports` je stav stránky a prežil by prepnutie -
  // v pätičke by potom visel počet vybraných podnetov, ktoré v tabuľke nie sú.
  React.useEffect(() => {
    setSelectedReports([]);
  }, [filters.archived]);

  // Hromadná akcia podľa výberu, nie podľa filtra: zoznam môže miešať
  // archivované so živými. Zmiešaný výber nemá akciu, ktorá by platila na
  // všetko - archivovať archivovaný aj obnoviť živý padá na 409.
  const selectionArchived = selectedReports.filter((r) => r.archived).length;
  const selectionMixed =
    selectionArchived > 0 && selectionArchived < selectedReports.length;
  const selectionAllArchived =
    selectedReports.length > 0 && selectionArchived === selectedReports.length;

  // ── Výber riadkov ──────────────────────────────────────────────────────────

  const selectedIds = React.useMemo(
    () => selectedReports.map((item) => item.reportId),
    [selectedReports],
  );

  const handleSelectionChange = React.useCallback(
    (next: (string | number)[]) => {
      const picked = new Set(next.map(String));

      setSelectedReports((prev) => [
        // Riadky z iných strán vo výbere zostávajú: duplikáty sa označujú aj
        // naprieč stránkovaním a `DataTable` vie len o svojej strane.
        ...prev.filter(
          (item) => !reports.some((row) => row.id === item.reportId),
        ),
        ...reports.filter((row) => picked.has(row.id)).map(toMergeReport),
      ]);
    },
    [reports],
  );

  // ── Stĺpce ─────────────────────────────────────────────────────────────────

  const hasLocationFilter = Boolean(location && distance);

  const columns: Column<ReportRow>[] = React.useMemo(
    () => [
      {
        key: 'id',
        label: __('ID'),
        // Identifikátor má **vždy** 7 znakov (`platform.identifier_length`
        // v `digiobcan-api`) a predpona sa v bunke nekreslí, takže stĺpec je
        // pevný, nie pružný - `fr` mu bralo šírku podľa okna a zvyšok riadku
        // o ňu prichádzal. Platí to odteraz aj pre kategórie: migrácia 000018
        // prepísala ich čitateľné kľúče na generované.
        // O 40 px širší než inde: pred identifikátorom stojí ešte náhľad
        // podnetu (32 px + medzera).
        width: '132px',
        minWidth: '132px',
        mono: true,
        render: (report) => {
          const preview = report.media.find((item) =>
            item.media_type.startsWith('image/'),
          )?.public_url;

          return (
            <Stack direction='row' alignItems='center' gap={1.5}>
              {/* Erb ako komponent, nie `/thumbnail.png`: v tom PNG bol podklad
                  zapečený, takže sa nedal zosvetliť. Rozhodnutie, kedy sa kreslí
                  namiesto obrázka - chýbajúca aj mŕtva adresa - drží
                  [[ErbThumbnail]], spolu s kartami v paneli. */}
              <ErbThumbnail
                src={preview}
                erbSize={18}
                sx={{
                  flexShrink: 0,
                  width: 32,
                  height: 32,
                  borderRadius: 1,
                  border: '1px solid',
                  borderColor: 'divider',
                }}
              />
              {/* Bez `Typography`: `variant='body2'` prebíja `fontFamily`
                aj `fontSize`, ktoré bunke dáva `mono` na stĺpci, a ID by
                tu bolo iným písmom než vo všetkých ostatných tabuľkách.
                Predpona `pdnt:` je v celom stĺpci rovnaká a rovnaká je aj
                v adrese, kam riadok vedie - v bunke stojí koncový segment. */}
              {shortId(report.id)}
            </Stack>
          );
        },
      },
      {
        key: 'title',
        label: __('Názov'),
        width: '2.4fr',
        minWidth: '240px',
        strong: true,
        render: (report) => (
          // `width: '100%'` a **žiadne** `alignItems: 'flex-start'`: obe vrstvy
          // `AdminReportCategories` sú mimo toku (`position: absolute`), takže
          // jeho obal do šírky nič neprispieva. Pri `flex-start` by sa stĺpec
          // zmrštil na šírku titulku a štítky by sa krátili podľa neho -
          // „Ostatné / Nezaradené" pri titulku „Test" vyšlo na „O…".
          <Stack gap={0.5} sx={{ minWidth: 0, width: '100%' }}>
            {/* `minWidth: 0` aj na tomto riadku: bez neho sa flexová položka
                nezmrští pod svoj obsah, titulok prerastie bunku a tabuľka ho
                len odstrihne - orezanie s tromi bodkami sa vôbec nespustí. */}
            <Stack
              direction='row'
              gap={0.5}
              alignItems='center'
              sx={{ minWidth: 0 }}
            >
              {report.explicit ? (
                <ExplicitIcon tooltip={__('Podnet obsahuje nevhodný obsah')} />
              ) : null}
              {/* Jeden riadok s tromi bodkami, nie zalamovanie: v riadku
                  tabuľky je pod titulkom ešte pás kategórií a druhý riadok
                  textu by ho posunul. `whiteSpace: nowrap` namiesto
                  `-webkit-box`: pri jednom riadku robí to isté, ale bodky
                  vykreslí aj tam, kde by sa dlhé slovo nezalomilo. */}
              <Typography
                variant='body2'
                title={report.title}
                sx={{
                  minWidth: 0,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {report.title}
              </Typography>
            </Stack>
            {/* Čo v riadku čaká práve na tohto úradníka: schválenie cudzieho
                vyjadrenia, alebo úprava vlastného, ktoré mu vrátili. Ani
                jedno nie je stav podnetu (ten zostáva V riešení) a iní
                úradníci to nevidia - preto štítok v riadku, nie v stĺpci
                stavu. Stojí **pred prvou kategóriou**, nie za názvom: tam ho
                dlhý názov odtlačil k okraju a kategórie začínajú v každom
                riadku na tom istom mieste, takže ho oko nájde bez hľadania.
                Kategórie dostanú zvyšok šírky (`flex: 1`), lebo si ju merajú,
                keď rozhodujú, koľko ich skryť do `+X`. */}
            <Stack
              direction='row'
              gap={0.5}
              alignItems='center'
              sx={{ minWidth: 0 }}
            >
              {report.awaiting_my_approval && (
                <AwaitingMePill>{AWAITING_APPROVAL_LABEL}</AwaitingMePill>
              )}
              {report.awaiting_my_revision && (
                <AwaitingMePill>{AWAITING_REVISION_LABEL}</AwaitingMePill>
              )}
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <AdminReportCategories issues={report.issues} />
              </Box>
            </Stack>
          </Stack>
        ),
      },
      {
        key: 'votes_count',
        // Stĺpec sa volá tým, čo v ňom stojí, nie tým, na čo sa používa:
        // „Priorita" znela ako vlastná škála úradu, hoci je to počet hlasov
        // občanov. Že sa podľa neho rozhoduje, čo vziať do ruky prvé, platí
        // ďalej - preto je zoraditeľný. Skloňovanie („10 hlasov") tu nemá čo
        // robiť: stĺpec je numerický a čísla sa porovnávajú okom pod sebou,
        // nie čítaním.
        label: __('Hlasy'),
        width: '0.6fr',
        minWidth: '90px',
        numeric: true,
        sortable: true,
        // Nula je platný počet, nie chýbajúca hodnota - `EMPTY_CELL` by tvrdil,
        // že sa hlasy nenačítali. Šedne, aby sa stĺpec dal prečítať pohľadom
        // zhora nadol a oko zastavilo len na tom, čo hlasy má.
        render: (report) => {
          const votes = report.votes_count ?? 0;
          return (
            <Typography
              variant='body2'
              color={votes > 0 ? 'text.primary' : 'text.disabled'}
            >
              {votes}
            </Typography>
          );
        },
      },
      ...(hasLocationFilter
        ? ([
            {
              key: 'distance',
              label: __('Vzdialenosť'),
              width: '0.8fr',
              minWidth: '110px',
              numeric: true,
              sortable: true,
              render: (report) =>
                typeof report.distance === 'number'
                  ? formatDistanceLabel(report.distance)
                  : null,
            },
          ] as Column<ReportRow>[])
        : []),
      {
        key: 'status',
        label: __('Stav podnetu'),
        width: '1fr',
        minWidth: '150px',
        // Archivácia stav podnetu **prebíja**: že mal naposledy nepridelený
        // nález, je pri podnete, ktorý je z aplikácie vonku, nepodstatné.
        // Derivovaný stav z nálezov API vracia ďalej a nie je to nesúlad -
        // nálezy si svoje stavy držia, mení sa len to, čo hlási stĺpec.
        render: (report) => (
          <AdminReportStatusItem
            status={report.archived_at ? 'archived' : report.status}
            variant='dot'
          />
        ),
      },
      {
        key: 'inserted_at',
        label: __('Dátum vytvorenia'),
        width: '1.1fr',
        minWidth: '170px',
        sortable: true,
        render: (report) =>
          formatDate(new Date(report.inserted_at), 'dd.MM.yyyy HH:mm'),
      },
      {
        key: 'deadline_at',
        label: __('Lehota'),
        width: '1.3fr',
        minWidth: '200px',
        sortable: true,
        render: (report) => (
          <DeadlineCell
            submittedAt={report.submitted_at}
            deadlineAt={report.deadline_at}
            overdue={report.overdue}
            issues={report.issues}
          />
        ),
      },
    ],
    [hasLocationFilter],
  );

  // ── Aktívne filtre ─────────────────────────────────────────────────────────

  /**
   * Chipy čítajú z tých istých hodnôt, aké sú v URL, a popis si berú zo
   * schémy (`optionLabel`) - v adrese je identifikátor, na obrazovke názov.
   */
  const activeChips = React.useMemo(() => {
    const chips: {
      key: string;
      label: string;
      value: string;
      remove: () => void;
    }[] = [];

    filterFields
      .filter((field) => field.type === 'select')
      .forEach((field) => {
        selectedValues(filters, field.key).forEach((value) => {
          chips.push({
            key: `${field.key}-${value}`,
            label: field.label,
            value: optionLabel(field, value),
            remove: () =>
              setFilters({
                ...filters,
                [field.key]: selectedValues(filters, field.key).filter(
                  (item) => item !== value,
                ),
              }),
          });
        });
      });

    // Prvý v poradí: kto má v zozname archív, to musí vidieť ako prvé - a v
    // lište je čip jediné miesto, kde sa to dá prečítať. „Stav podnetu", lebo
    // archív je člen filtra stavu, rovnako ako „Stav nálezu: Duplikáty".
    if (filters.archived) {
      chips.push({
        key: 'archived',
        label: __('Stav podnetu'),
        value: ARCHIVED_LABEL,
        remove: () => setFilters({ ...filters, archived: false }),
      });
    }

    if (filters.dateFrom || filters.dateTo) {
      chips.push({
        key: 'date',
        label: __('Dátum'),
        value: [filters.dateFrom, filters.dateTo]
          .filter(Boolean)
          .map((value) => formatDate(new Date(value as string), 'dd.MM.yyyy'))
          .join(' - '),
        remove: () => setFilters({ ...filters, dateFrom: '', dateTo: '' }),
      });
    }

    if (filters.overdue) {
      chips.push({
        key: 'overdue',
        label: __('Lehota'),
        value: OVERDUE_LABEL,
        remove: () => setFilters({ ...filters, overdue: false }),
      });
    }

    if (filters.duplicates) {
      chips.push({
        key: 'duplicates',
        label: __('Stav nálezu'),
        value: DUPLICATES_LABEL,
        remove: () => setFilters({ ...filters, duplicates: false }),
      });
    }

    if (isApprover && filters.awaitingMyApproval) {
      chips.push({
        key: 'awaitingMyApproval',
        label: __('Schvaľovanie'),
        value: AWAITING_APPROVAL_LABEL,
        remove: () => setFilters({ ...filters, awaitingMyApproval: false }),
      });
    }

    if (isInAssigningCategory && filters.assignedToMe) {
      chips.push({
        key: 'assignedToMe',
        label: __('Riešiteľ'),
        value: ASSIGNED_TO_ME_LABEL,
        remove: () => setFilters({ ...filters, assignedToMe: false }),
      });
    }

    if (location && distance) {
      chips.push({
        key: 'location',
        label: __('Lokalita'),
        value: `${encode({ latitude: location[1], longitude: location[0] })} (${formatDistanceLabel(distance)})`,
        remove: () => setLocationFilter({}),
      });
    }

    if (search) {
      chips.push({
        key: 'q',
        label: __('Hľadaný výraz'),
        value: search,
        remove: () => setQuery(''),
      });
    }

    return chips;
  }, [
    filterFields,
    filters,
    location,
    distance,
    search,
    setFilters,
    setLocationFilter,
    setQuery,
    isApprover,
    isInAssigningCategory,
  ]);

  // Boolean, nie počet: prázdny stav sa mení len medzi „sú filtre" a „nie sú",
  // takže tabuľku netreba prekresľovať pri každom pridanom chipe.
  const hasFilters = activeChips.length > 0;

  /**
   * Riadok je odkaz, nie klik: `rowHref` z knižnice z neho spraví `<a href>`,
   * takže sa podnet dá cmd klikom otvoriť v novej karte - v administrácii sa
   * podnety porovnávajú vedľa seba. Routovanie vybaví `LinkProvider`, ktorý
   * inštaluje shell zo svojho `onNavigate`.
   */
  const rowHref = React.useCallback(
    (report: ReportRow) => `/administracia/podnety/${shortId(report.id)}`,
    [],
  );

  /**
   * Tabuľka je vo `useMemo`, nie priamo v JSX. Písanie do hľadania prekresľuje
   * túto komponentu pri každej klávese, ale `reports`, `columns`, `sort` ani
   * výber sa pritom nemenia (dotaz ide až po debounce) - bez memoizácie by
   * každá klávesa znova poskladala 25 riadkov po ôsmich bunkách, z toho tri
   * s `Tooltip`om, a písanie by sekalo.
   */
  const table = React.useMemo(
    () => (
      <DataTable
        variant={isMd ? 'table' : 'cards'}
        columns={columns}
        rows={reports}
        rowKey='id'
        sort={sort}
        onSortChange={setSort}
        selected={selectedIds}
        onSelectionChange={handleSelectionChange}
        rowHref={rowHref}
        // Čaká na mňa - schválenie alebo úprava vráteného vyjadrenia. Jediné
        // veci v zozname, ktoré čakajú na konkrétneho človeka, preto
        // podfarbený celý riadok, nie len štítok. Štítok zostáva: hovorí,
        // *prečo* riadok svieti.
        rowTone={(report) =>
          report.awaiting_my_approval || report.awaiting_my_revision
            ? 'warning'
            : undefined
        }
        sortLabel={__('Zoradiť:')}
        selectAllLabel={__('Označiť všetky podnety na strane')}
        selectRowLabel={__('Označiť podnet')}
        loading={isLoading || isFetchingNextPage}
        loadingRows={limit > 12 ? 12 : limit}
        empty={
          <EmptyState
            compact
            icon={hasFilters ? 'search' : 'list'}
            title={
              // Archív má vlastný text: „Žiadny podnet nesedí na filtre" by
              // pri prepnutom archíve znelo ako chyba filtrovania, hoci
              // prázdny archív je normálny a dobrý stav.
              viewingArchive
                ? __('Archív je prázdny')
                : viewingApprovals
                  ? __('Nič nečaká na vaše schválenie')
                  : hasFilters
                    ? __('Žiadny podnet nesedí na filtre')
                    : __('Žiadne podnety')
            }
            description={
              viewingArchive
                ? __(
                    'V kategóriách, ktoré spravujete, nie je archivovaný žiadny podnet.',
                  )
                : viewingApprovals
                  ? __(
                      'Keď kolega pošle vyjadrenie na schválenie, objaví sa tu a príde vám o ňom notifikácia.',
                    )
                  : hasFilters
                    ? __('Skúste uvoľniť niektorý z aktívnych filtrov.')
                    : __('V kategóriách, ktoré spravujete, zatiaľ nič nie je.')
            }
            actions={
              hasFilters ? (
                <Button variant='outline' size='small' onClick={clearAll}>
                  {__('Zrušiť filtre')}
                </Button>
              ) : undefined
            }
          />
        }
      />
    ),
    [
      isMd,
      limit,
      columns,
      reports,
      sort,
      setSort,
      selectedIds,
      handleSelectionChange,
      rowHref,
      isLoading,
      isFetchingNextPage,
      hasFilters,
      viewingArchive,
      viewingApprovals,
      clearAll,
    ],
  );

  // ── Pätička ────────────────────────────────────────────────────────────────

  const hasNext =
    (pageIndex < data.pages.length - 1 || hasNextPage) && !isFetchingNextPage;

  const footerLeft = React.useMemo(
    () => (
      <>
        {selectedReports.length > 0 ? (
          <>
            <Typography
              variant='body2'
              sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}
            >
              {__('Vybrané:')} {selectedReports.length}
            </Typography>
            {/* Zlučovanie sa nad archivovaným podnetom neponúka: padá na
                409, takže tlačidlo by viedlo len k chybe. */}
            {!selectionArchived && (
              /* Duplikát je vzťah dvoch nálezov, takže z jedného podnetu sa
                 označiť nedá - primárne sa vyberá z tých vybratých. */
              <Tooltip
                title={
                  selectionHasUnmanaged
                    ? __('Vo výbere je podnet, ktorý riešia v inej kategórii.')
                    : selectedReports.length > 1
                      ? ''
                      : __('Označ aspoň dve podnety - jedno bude primárne.')
                }
              >
                <Box component='span' sx={{ display: 'inline-flex' }}>
                  <Button
                    size='small'
                    variant='contained'
                    disabled={
                      selectedReports.length < 2 || selectionHasUnmanaged
                    }
                    onClick={() => setMergeModalReports(selectedReports)}
                  >
                    {__('Označiť ako duplikáty')}
                  </Button>
                </Box>
              </Tooltip>
            )}
            {/* Archivácia stačí na jednom podnete, na rozdiel od duplicity -
                nie je to vzťah, ale rozhodnutie o každom zvlášť. V archíve je
                to tá istá akcia opačným smerom. */}
            {canArchive && (
              <Tooltip
                title={
                  selectionMixed
                    ? __(
                        'Vo výbere sú archivované aj živé podnety - vyberte len jedny.',
                      )
                    : ''
                }
              >
                <Box component='span' sx={{ display: 'inline-flex' }}>
                  <Button
                    size='small'
                    variant='outline'
                    disabled={selectionHasUnmanaged || selectionMixed}
                    onClick={() =>
                      setArchiveModalReports(
                        selectedReports.map((report) => ({
                          reportId: report.reportId,
                          title: report.title,
                        })),
                      )
                    }
                  >
                    {selectionAllArchived ? __('Obnoviť') : __('Archivovať')}
                  </Button>
                </Box>
              </Tooltip>
            )}
            {canRunAi && (
              <Tooltip
                title={
                  selectedReports.length > AI_DRY_RUN_MAX
                    ? `${__('Naraz najviac')} ${AI_DRY_RUN_MAX} ${__('podnetov.')}`
                    : __(
                        'Skúšobný beh AI - výsledok v rozhodnutiach AI, podnety sa nezmenia.',
                      )
                }
              >
                <Box component='span' sx={{ display: 'inline-flex' }}>
                  <Button
                    size='small'
                    variant='outline'
                    disabled={selectedReports.length > AI_DRY_RUN_MAX}
                    onClick={() =>
                      setAiDryRunReports(
                        selectedReports.map((report) => ({
                          reportId: report.reportId,
                          title: report.title,
                        })),
                      )
                    }
                  >
                    {__('AI vyhodnotenie')}
                  </Button>
                </Box>
              </Tooltip>
            )}
            <Button
              size='small'
              variant='text'
              onClick={() => setSelectedReports([])}
            >
              {__('Zrušiť výber')}
            </Button>
          </>
        ) : null}

        {sort.map((item) => (
          <FilterChip
            key={item.key}
            label={__('Zoradené')}
            value={`${
              columns.find((column) => column.key === item.key)?.label ??
              item.key
            } ${item.dir === 'asc' ? '↑' : '↓'}`}
            onRemove={() => setSort([])}
          />
        ))}
      </>
    ),
    [
      selectedReports,
      selectionHasUnmanaged,
      sort,
      columns,
      setSort,
      selectionArchived,
      selectionMixed,
      selectionAllArchived,
      canArchive,
      canRunAi,
    ],
  );

  React.useEffect(() => {
    setFooter(
      <Pagination
        page={pageIndex}
        hasNext={hasNext}
        onPageChange={(next) => {
          // Ďalšia strana ešte nemusí byť načítaná - `useInfiniteQ` drží
          // stránky za sebou, `pageIndex` je okno do nich.
          if (next >= data.pages.length && hasNextPage) {
            fetchNextPage();
          }
          setPageIndex(next);
        }}
        sortSummary={footerLeft}
      />,
    );

    return () => setFooter(null);
  }, [
    setFooter,
    setPageIndex,
    pageIndex,
    hasNext,
    hasNextPage,
    fetchNextPage,
    data.pages.length,
    footerLeft,
  ]);

  /**
   * Na desktope je filter **trvalý stĺpec v shelle**, nie panel nad obsahom:
   * ide do `aside`, aby začínal na úrovni hlavičky stránky, nie pod ňou.
   * Preto v lište nie je tlačidlo „Všetky filtre" - panel je otvorený vedľa -
   * a preto nemá ani pätičku: nič nezakrýva, takže „Použiť" by nemalo čo
   * zavrieť a filtre sa uplatňujú hneď pri zmene poľa.
   * Čipy aktívnych filtrov v nej ale sú, rovnako ako na mobile: rýchle filtre
   * hlásia len počet („Kategória 2") a panel má tie svoje schované, takže bez
   * čipov sa nedalo prečítať, podľa čoho je zoznam vlastne vyfiltrovaný.
   *
   * Na mobile sa panel nezmestí, takže tam ostáva ako sheet.
   */
  React.useEffect(() => {
    if (!isMd) {
      setAside(null);
      return;
    }

    setAside(
      <FilterPanel
        variant='inline'
        fields={filterFields}
        // Rýchle polia sú v lište v hlavičke; v paneli by tá istá hodnota stála
        // druhýkrát o dvadsať pixelov vedľa.
        hideQuickFields
        value={filters}
        onChange={setFilters}
        // Bez `onClear` a bez popisov tlačidiel: trvalý stĺpec pätičku nemá.
        // Filtre sa uplatňujú okamžite a ruší sa každý zvlášť v riadku čipov
        // nad zoznamom.
        title={__('Filtrovanie')}
        defaultGroupLabel={__('Filtre')}
        childrenLabel={__('Lokalita a okolie')}
      >
        <ReportsLocationFilter
          value={{ location, distance }}
          onChange={setLocationFilter}
        />
      </FilterPanel>,
    );

    return () => setAside(null);
  }, [
    isMd,
    setAside,
    filterFields,
    filters,
    setFilters,
    location,
    distance,
    setLocationFilter,
  ]);

  /**
   * Tlač a export sú **akcia stránky** (`setActions`), nie súčasť lišty
   * filtrov.
   *
   * Lišta patrí zoznamu - hľadanie, filtre, zoradenie - a tlač zoznam nemení,
   * len ho vydá von. Je to tá istá deliaca čiara, podľa ktorej je „Vytvoriť"
   * akciou stránky a „Zrušiť filtre" nie ([[zoznamy]]).
   *
   * Kreslí sa **len tomu, kto zoznam vidí**: bez `pruser:reports:read` je
   * stránka aj tak prázdna a tlačidlo by sľubovalo dokument, ktorý API
   * odmietne.
   *
   * Vedľa neho je vstup do kategórií podnetov. V navigácii už nie sú - patria
   * k podnetom, a kto ich spravovať nesmie, tlačidlo nevidí.
   */
  React.useEffect(() => {
    setActions([
      ...(canManageCategories
        ? [{ children: <ReportCategoriesButton /> }]
        : []),
      ...(canReadReports
        ? [{ children: <ReportsExportButton query={reportsQuery} /> }]
        : []),
    ]);

    return () => setActions([]);
  }, [setActions, canReadReports, canManageCategories, reportsQuery]);

  /**
   * Lišta filtrov ide do **hlavičky stránky**, nie na začiatok obsahu: hlavička
   * stojí nad skrolovacím obalom, takže hľadanie a rýchle filtre zostanú pri
   * skrolovaní tabuľky na obrazovke. Kým bola lišta prvým blokom obsahu,
   * odskrolovala sa s ním.
   *
   * Rýchle filtre sú v nej **aj na desktope**, hoci tie isté polia sú o kúsok
   * vedľa v trvalom paneli. Panel ich tam preto už nekreslí (`hideQuickFields`)
   * - hodnota je na jednom mieste, len je to lišta a nie panel. Pod hľadaním
   * stojí vyhradený riadok s čipmi aktívnych filtrov (aj hľadaného výrazu,
   * lokality a dátumu, teda aj toho, čo v lište vidieť nie je); prázdny sa
   * nekreslí. Tlačidlo „Všetky filtre" na desktope netreba - panel je
   * otvorený vedľa.
   */
  React.useEffect(() => {
    setPageFilters(
      <FilterBar
        variant='header'
        fields={filterFields}
        value={filters}
        onChange={setFilters}
        query={query}
        onQueryChange={setQuery}
        searchPlaceholder={__('Hľadať podľa ID, názvu alebo popisu')}
        // Prepínač, nie len otvorenie: panel sa zavrie klikom kamkoľvek mimo
        // neho, a tlačidlo, ktorým sa otvoril, je „mimo" tiež.
        onOpenPanel={isMd ? undefined : () => setPanelOpen((open) => !open)}
        activeCount={activeCount + (location && distance ? 1 : 0)}
        panelLabel={__('Všetky filtre')}
      >
        {activeChips.map((chip) => (
          <FilterChip
            key={chip.key}
            label={chip.label}
            value={chip.value}
            onRemove={chip.remove}
          />
        ))}
      </FilterBar>,
    );

    return () => setPageFilters(null);
  }, [
    setPageFilters,
    filterFields,
    filters,
    setFilters,
    query,
    setQuery,
    isMd,
    activeCount,
    location,
    distance,
    activeChips,
  ]);

  return (
    <>
      {/* Plátno pod tabuľkou kreslí shell (scrollovací obal), takže siaha po
          pätičku aj pri krátkom zozname - tu ostáva len odsadenie. */}
      <Box sx={{ padding: 1.5 }}>{table}</Box>

      {panelOpen && !isMd ? (
        <FilterPanel
          variant='sheet'
          fields={filterFields}
          hideQuickFields
          value={filters}
          onChange={setFilters}
          onClose={() => setPanelOpen(false)}
          onClear={clearAll}
          applyLabel={__('Použiť')}
          clearLabel={__('Vymazať filtre')}
          title={__('Filtrovanie')}
          closeLabel={__('Zavrieť filtre')}
          defaultGroupLabel={__('Filtre')}
          childrenLabel={__('Lokalita a okolie')}
        >
          <ReportsLocationFilter
            value={{ location, distance }}
            onChange={setLocationFilter}
          />
        </FilterPanel>
      ) : null}

      <AdminReportsArchiveModal
        reports={archiveModalReports}
        mode={selectionAllArchived ? 'restore' : 'archive'}
        onClose={() => setArchiveModalReports([])}
        onSuccess={() => {
          setSelectedReports([]);
          setArchiveModalReports([]);
          refetchData();
        }}
      />

      <AdminReportsAiDryRunModal
        reports={aiDryRunReports}
        onClose={() => setAiDryRunReports([])}
        onSuccess={() => {
          setSelectedReports([]);
          setAiDryRunReports([]);
        }}
      />

      <AdminReportsMergeModal
        reports={mergeModalReports}
        onClose={() => setMergeModalReports([])}
        onSuccess={() => {
          setSelectedReports([]);
          setMergeModalReports([]);
          refetchData();
        }}
      />
    </>
  );
};

export default ReportsAdminPage;
